// =====================================================================
// parametrosManual.js
// Parámetros de las fórmulas del cotizador manual (Excel
// Cotizador_VV170326.xlsm) extraídos a un objeto reactivo editable
// desde la vista de Configuraciones (solo admin).
//
// Los valores viven en la BD (tabla cotizador."ParametrosCotizador",
// endpoints /api/parametros-cotizador) y se propagan a TODOS los
// usuarios. calculos.js lee SIEMPRE de `parametros`, por lo que
// cualquier cambio recalcula las cotizaciones al instante.
// Mientras la API responde (o si falla), se usan los valores de
// fábrica de PARAMETROS_DEFAULT.
// =====================================================================

import { reactive, ref } from "vue"
import api from "@/api/axios"

export const PARAMETROS_DEFAULT = {
  general: {
    fee: 1.15,        // recargo aplicado a vuelos, asistencia, tours y traslados (15%)
    redondeo: 1000    // MROUND del precio final en COP
  },

  internacional: {
    asistenciaUsdDia: 1.4
  },

  nacional: {
    gastoCopPaxDia: 1500
  },

  mex: {
    asistenciaUsdDia: 1.7,
    trasladosUsdPax: 83,
    toursHasta3: 100,
    tours4: 140,
    tours5a10: 340,
    tours11a19: 400,
    tours20a30: 780,
    toursUsdPaxDesde31: 25
  },

  cancun: {
    asistenciaUsdDia: 1.7,
    trasladosHasta7: 250000,
    traslados8a14: 500000,
    traslados15a16: 700000,
    trasladosCopPaxDesde17: 50000
  },

  playaDelCarmen: {
    asistenciaUsdDia: 1.7,
    trasladosHasta7: 700000,
    traslados8a14: 1400000,
    traslados15a16: 1250000,
    trasladosCopPaxDesde17: 80000
  },

  puntaCana: {
    asistenciaUsdDia: 1.2,
    trasladosHasta4: 20,
    traslados5a6: 30,
    traslados7a12: 40,
    trasladosUsdPaxDesde13: 6
  },

  cuba: {
    asistenciaUsdDia: 1.2,
    trasladosVaraderoUsd: 57,
    trasladosHabanaUsd: 65,
    trasladosCombinadoUsd: 109
  },

  panama: {
    asistenciaUsdDia: 1.2,
    ivaTraslados: 1.19,
    playaSoloUsd: 100,
    playaGrupoUsd: 50,
    ciudadSoloUsd: 56,
    ciudadGrupoUsd: 28,
    combinadoSoloUsd: 136,
    combinadoGrupoUsd: 68
  },

  otros: {
    feeHotel: 1.06,
    asistenciaUsdDia: 1.4
  },

  turquia: {
    tarifaParejaUsd: 699,
    factorTrm: 0.85,
    asistenciaUsdDia: 1.4,
    margen: 1.2,
    ivaNochesAdicionales: 1.18,
    diasAntesSalida: 1,
    diasPlan: 10
  },

  tailandia: {
    tarifaParejaUsd: 3329,
    tarifaSencillaUsd: 2409,
    tarifaNinoUsd: 1429,
    factorTrm: 0.88,
    margen: 1.2,
    ivaNochesAdicionales: 1.18,
    diasAntesSalida: 2,
    diaVueloInterno: 9,
    diaRegreso: 13
  },

  sanAndres: {
    factorComision: 0.87,
    iva: 1.18
  },

  costa: {
    ivaSoloDesayuno: 1.19,
    ivaAlimentacion: 1.18,
    descuentoBase: 0.14
  },

  gruposTravel: {
    factorHotel: 1.18
  },

  crucero: {
    noches: 7,
    fechaCambioNaviera: "2026-05-03",
    navieraAntes: "SERENADE OF THE SEAS - ROYAL CARIBBEAN",
    navieraDespues: "GRANDEUR OF THE SEAS - ROYAL CARIBBEAN"
  }
}

/**
 * Combina lo guardado sobre los valores de fábrica, conservando la
 * estructura de PARAMETROS_DEFAULT (parámetros nuevos toman su default).
 */
function combinar(base, extra) {
  const out = {}
  for (const k of Object.keys(base)) {
    if (base[k] !== null && typeof base[k] === "object") {
      out[k] = combinar(base[k], extra?.[k])
    } else {
      out[k] = extra?.[k] ?? base[k]
    }
  }
  return out
}

/** Copia en profundidad `origen` dentro del objeto reactivo `destino`. */
function asignar(destino, origen) {
  for (const k of Object.keys(origen)) {
    if (origen[k] !== null && typeof origen[k] === "object") {
      asignar(destino[k], origen[k])
    } else {
      destino[k] = origen[k]
    }
  }
}

export const parametros = reactive(combinar(PARAMETROS_DEFAULT, null))

/** true cuando ya se intentó cargar desde la API al menos una vez. */
export const parametrosCargados = ref(false)

/** Fecha de la última actualización guardada en la BD (o null). */
export const parametrosActualizadosEn = ref(null)

let promesaCarga = null

/**
 * Carga los parámetros desde la BD. Reutiliza la petición en curso
 * salvo que se pida `forzar` (ej: al abrir la vista de configuración).
 * Si la API falla se mantienen los valores actuales (defaults).
 */
export function cargarParametros(forzar = false) {
  if (promesaCarga && !forzar) return promesaCarga

  promesaCarga = api
    .get("/parametros-cotizador")
    .then(({ data }) => {
      asignar(parametros, combinar(PARAMETROS_DEFAULT, data.parametros))
      parametrosActualizadosEn.value = data.updatedAt ?? null
      return parametros
    })
    .catch(err => {
      console.error("No se pudieron cargar los parámetros del cotizador:", err)
      return parametros
    })
    .finally(() => {
      parametrosCargados.value = true
    })

  return promesaCarga
}

/** Guarda en la BD (solo admin) y aplica de inmediato en esta sesión. */
export async function guardarParametros(nuevos) {
  const combinados = combinar(PARAMETROS_DEFAULT, nuevos)
  await api.put("/parametros-cotizador", { parametros: combinados })
  asignar(parametros, combinados)
}

/** Borra lo guardado en la BD (solo admin) y vuelve a los defaults. */
export async function restaurarParametros() {
  await api.delete("/parametros-cotizador")
  asignar(parametros, PARAMETROS_DEFAULT)
  parametrosActualizadosEn.value = null
}
